import axios from 'axios'

const API_BASE = process.env.VUE_APP_API_URL || '/api'

const client = axios.create({
  baseURL: API_BASE,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json'
  }
})

client.interceptors.response.use(
  response => response.data,
  error => Promise.reject(error)
)

const GROUP_ORDER = [
  'market_event_watch',
  'stock_event_watch',
  'position_context_watch',
  'system_health_check'
]

const GROUP_TASK_TYPES = {
  market_event_watch: ['news_watch'],
  stock_event_watch: ['announcement_watch', 'stock_news_watch', 'irm_qa_watch'],
  position_context_watch: [],
  system_health_check: ['system_health_check']
}

const DISPLAY_NAMES = {
  market_event_watch: '市场事件监控',
  stock_event_watch: '个股事件监控',
  position_context_watch: '持仓上下文监控',
  system_health_check: '系统健康检查'
}

function unwrapPayload(res) {
  return (res && (res.machine_payload || res.data || res.payload)) || res || {}
}

function extractList(res, keys) {
  if (Array.isArray(res)) return res
  const payload = unwrapPayload(res)
  if (Array.isArray(payload)) return payload
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key]
  }
  return []
}

function normalizeTime(value) {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{1,2})$/)
  if (!match) return ''
  const hour = Number(match[1])
  const minute = Number(match[2])
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return ''
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function scheduleTimes(tasks) {
  const values = []
  tasks.forEach(task => {
    String(task.schedule || '')
      .split(',')
      .map(normalizeTime)
      .filter(Boolean)
      .forEach(value => values.push(value))
  })
  return [...new Set(values)].sort()
}

function configStatus(groupId, tasks) {
  if (groupId === 'position_context_watch') {
    return { value: 'not_connected', text: '尚未接入' }
  }
  if (!tasks.length) return { value: 'incomplete', text: '配置不完整' }
  const statuses = tasks.map(task => task.status)
  if (statuses.every(status => status === 'active')) {
    return { value: 'enabled', text: '已启用' }
  }
  if (statuses.every(status => status === 'paused')) {
    return { value: 'disabled', text: '已停用' }
  }
  return { value: 'partial', text: '部分启用' }
}

function scopeSummary(groupId, tasks) {
  if (groupId === 'market_event_watch') return '全市场重大事件（系统管理）'
  if (groupId === 'position_context_watch') return '尚未接入'
  if (groupId === 'system_health_check') return '系统级检查'
  const scopes = [...new Set(tasks.map(task => task.scope_type).filter(Boolean))]
  if (scopes.length > 1) return '范围配置不一致'
  if (scopes[0] === 'formal_all') return '第一本账正式区'
  if (scopes[0] === 'first_book_all') return '第一本账全范围'
  if (scopes[0] === 'custom') return '自定义范围'
  return '第一本账全范围'
}

function runTimeValue(run) {
  if (!run) return ''
  return run.run_time_bj || run.run_time || run.scheduled_for || run.started_at || run.completed_at || run.created_at_bj || run.created_at || ''
}

function runTimeMs(run) {
  let value = String(runTimeValue(run) || '').trim().replace(' ', 'T')
  if (!value) return 0
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(value)) value += '+08:00'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 0 : date.getTime()
}

function normalizeProductLatestRuns(response) {
  const payload = unwrapPayload(response)
  if (!Array.isArray(payload.groups)) return response

  // The adapter owns whether the product is using the authoritative summary or
  // the compatibility fallback. Always surface that state to the UI.
  if (!payload.data_status) payload.data_status = 'official'

  payload.groups = payload.groups.map(group => {
    const parent = group.display_latest_run || group.latest_parent_run || null
    const child = group.latest_child_run || null
    if (!parent || !child) return group

    const parentTime = runTimeMs(parent)
    const childTime = runTimeMs(child)
    if (!parentTime || !childTime) return group

    const difference = childTime - parentTime
    if (difference >= 0 && difference <= 30 * 60 * 1000) {
      // 同一轮内，父任务回执才是产品层结果；子任务仅用于详情。
      return {
        ...group,
        display_latest_run: parent,
        latest_child_run: null
      }
    }
    if (childTime > parentTime) {
      // 子任务来自更晚一轮（例如系统健康今天 09:00，而旧父回执是昨天），
      // 允许它补充最新状态。
      return {
        ...group,
        display_latest_run: child
      }
    }
    return group
  })
  return response
}

function normalizeLegacySummary(groupPayload, runsPayload) {
  const legacyGroups = extractList(groupPayload, ['groups', 'items', 'data'])
  const runs = extractList(runsPayload, ['runs', 'items', 'data'])

  return {
    version: 'notification_task_group_summary_frontend_legacy_fallback_v2',
    data_status: 'fallback',
    groups: GROUP_ORDER.map((groupId, index) => {
      const legacy = legacyGroups.find(item => item.group_id === groupId) || {}
      const expectedTypes = GROUP_TASK_TYPES[groupId]
      const tasks = Array.isArray(legacy.tasks)
        ? legacy.tasks.filter(task => expectedTypes.includes(task.task_type))
        : []
      const groupRuns = runs
        .filter(run => expectedTypes.includes(run.task_type))
        .sort((a, b) => String(runTimeValue(b)).localeCompare(String(runTimeValue(a))))
      const latestRun = groupRuns[0] || null
      const status = configStatus(groupId, tasks)
      const times = scheduleTimes(tasks)
      const running = groupRuns.some(run => String(run.status || '').toLowerCase() === 'running')
      const runtimeStatus = groupId === 'position_context_watch'
        ? 'not_connected'
        : (running ? 'running' : (latestRun ? 'idle' : 'no_record'))
      const runtimeStatusText = groupId === 'position_context_watch'
        ? '尚未接入'
        : (running ? '执行中' : (latestRun ? '当前空闲' : '尚无记录'))

      return {
        group_id: groupId,
        display_name: legacy.display_name || DISPLAY_NAMES[groupId],
        group_order: index + 1,
        expected_task_types: expectedTypes,
        expected_task_count: expectedTypes.length,
        actual_task_count: tasks.length,
        active_task_count: tasks.filter(task => task.status === 'active').length,
        paused_task_count: tasks.filter(task => task.status === 'paused').length,
        config_status: status.value,
        config_status_text: status.text,
        runtime_status: runtimeStatus,
        runtime_status_text: runtimeStatusText,
        scope: {
          display_mode: groupId === 'position_context_watch' ? 'not_connected' : 'fallback',
          summary: scopeSummary(groupId, tasks),
          configured_scope_types: groupId === 'market_event_watch'
            ? ['system_managed']
            : [...new Set(tasks.map(task => task.scope_type).filter(Boolean))],
          consistent: true,
          execution_semantics_supported: groupId !== 'position_context_watch'
        },
        schedule: {
          aggregation_mode: 'union_of_child_schedules',
          parse_status: 'exact',
          consistent: true,
          times,
          summary: groupId === 'position_context_watch'
            ? '尚未接入'
            : (times.length ? `每日 ${times.join('、')}` : '尚未配置')
        },
        notification: {
          primary_channel: 'feishu',
          channels: ['feishu'],
          notify_modes: [...new Set(tasks.map(task => task.notify_mode).filter(Boolean))],
          dedupe_required: true,
          consistent: true
        },
        latest_child_run: latestRun,
        tasks,
        integrity_issues: [],
        capabilities: {
          view_summary: true,
          view_records: true,
          configure_preview: false,
          configure_apply: false,
          enable_disable_apply: false,
          run_once_preview: false,
          run_once_execute: false,
          atomic_group_update: false,
          atomic_group_run: false,
          parent_run_receipt: false
        }
      }
    })
  }
}

async function getSummaryWithFallback() {
  try {
    const response = await client.get('/notification-task-groups/summary')
    const payload = unwrapPayload(response)
    if (Array.isArray(payload.groups) && payload.groups.length === 4) {
      return normalizeProductLatestRuns(response)
    }
  } catch (error) {
    // Continue to the backward-compatible read-only fallback below.
  }

  const [groupPayload, runsPayload] = await Promise.all([
    client.get('/notification-task-groups/'),
    client.get('/scheduled-tasks/runs/', { params: { limit: 100 } })
  ])
  return normalizeProductLatestRuns(normalizeLegacySummary(groupPayload, runsPayload))
}

export const notificationTaskGroupApi = {
  getSummary: getSummaryWithFallback,

  getOwnerStatus: () => (
    client.get('/notification-scheduler-owner-status/', {
      params: { stale_after_seconds: 45 }
    })
  ),

  previewSettings: (groupId, data) => (
    client.post(`/notification-task-groups/${groupId}/settings/preview`, data)
  ),

  getSettingsOptions: (groupId) => (
    client.get(`/notification-task-groups/${groupId}/settings/options`)
  ),

  previewProductionSettings: (groupId, data) => (
    client.post(`/notification-task-groups/${groupId}/settings/production-preview`, data)
  ),

  applySettings: (groupId, data) => (
    client.post(`/notification-task-groups/${groupId}/settings/apply`, data)
  ),

  getParentRuns: (params = {}) => (
    client.get('/notification-parent-runs-readonly/', { params })
  ),

  getParentRun: (parentRunId) => (
    client.get(`/notification-parent-runs-readonly/${encodeURIComponent(parentRunId)}`)
  )
}

export default notificationTaskGroupApi
